// Core packages
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

// Third party packaged
import { MatDialog } from '@angular/material';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from 'src/app/shared/services/helper.service';
import { ThemeService } from 'src/app/shared/services/theme.service';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.page.html',
  styleUrls: ['./forgot-password.page.scss']
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add property form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('auth.forgotPassword.form.submitButtonText'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-arrow-right',
      inline: true,
    },
  };
  // END - Add property form

  /**
   * Class constructor
   */
  constructor(
    private themeService: ThemeService,
    private authService: AuthService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public helperService: HelperService,
    public configService: ConfigService,
    private dialog: MatDialog,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Subscribe to current lang and init component
    this.lang = this.translateService.currentLang;
    if (this.activatedRoute.params) {
      this.subscriptions.push(
        this.activatedRoute.params.subscribe(
          res => {
            if (res) {
              if (res.lang) {
                this.lang = res.lang;
              }
              this.handleRouteParamsChange();
            }
          },
          err => {
            // if we came here users with given userId do not exist
            this.router.navigate([this.lang, 'errors', '404']);
          }
        )
      );
    }
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init component
   */
  handleRouteParamsChange(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('auth.forgotPassword.pageTitle'));

    // Prevent form fields to be rendered twice on language change
    this.fields = [];

    // Initialize form
    this.initForm();

    // Set correct button text based on current language
    this.formBtnOptions.text = this.translateService.instant('auth.forgotPassword.form.submitButtonText');

    // Check if user is already logged in with authService.
    // If so, redirect him to home
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(
        (user) => {
          if (user) {
            this.loggedUser = user;
            this.router.navigate([this.lang, 'app']);
          }
        }
      )
    );

    // Check if user is logged in with cookies
    // data. If so, redirect him
    this.subscriptions.push(
      this.authService.checkLogin().subscribe(
        res => {
          if (res.status) {
            // User already logged, redirect him to home
            this.router.navigate([this.lang, 'app']);
          }
        }
      )
    );
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = {
      email: '',
    };

    // Set fields
    this.fields = [
      ...this.fields,
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12',
            type: 'input',
            key: 'email',
            templateOptions: {
              label: this.translateService.instant('auth.login.form.email.label'),
              placeholder: this.translateService.instant('auth.login.form.email.placeholder'),
              required: true,
              maxLength: 320,
              type: 'email',
              pattern: this.configService.emailPattern,
            },
          },
        ],
      },
    ];
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(this.translateService.instant('common.errors.form_is_invalid'));
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    this.subscriptions.push(
      this.authService.recoverPassword(model.email)
        .pipe(
          finalize(() => this.formBtnOptions.active = false)
        )
        .subscribe(
          res => {
            if (res.status) {
              const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                data: {
                  title: this.translateService.instant('dialogs.resetPassword.title'),
                  message: this.translateService.instant('dialogs.resetPassword.message'),
                  btnOkText: this.translateService.instant('dialogs.resetPassword.login'),
                  btnCancelText: this.translateService.instant('common.cta.close'),
                },
                width: '500px',
                disableClose: true
              });
              // Subscribe to dialog result (only for 1 emit thanks to "take()")
              dialogRef.beforeClosed()
                .pipe(
                  take(1)
                )
                .subscribe(
                  res2 => {
                    if (res2) {
                      // User hit confirm button. We can redirect him
                      this.router.navigate([this.lang, 'auth', 'login']);
                    }
                  }
                );
              return;
            }

            // Check if back-end is up and is running
            this.toastrService.error(this.translateService.instant('common.errors.server_down'));
            return;
          },
          err => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }
}
